import { useEffect, useState, type ComponentProps } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { LOGIN_ROUTE_PATH } from '../routes/appRoutes';
import { resetPasswordByEmailLink, verifyPasswordResetToken } from '../services/authService';

type EmailLinkMode = 'signup' | 'reset';

const EXPIRED_LINK_ERROR_CODE = '40001006';
const INVALID_LINK_ERROR_CODE = '40001001';
const USED_LINK_ERROR_CODE = '40001002';

function getPasswordResetErrorMessage(error: unknown) {
  if (error instanceof ApiError || error instanceof Error) {
    return error.message;
  }

  return '비밀번호 재설정 처리 중 오류가 발생했습니다.';
}

function getPasswordLinkErrorMessage(error: unknown) {
  if (error instanceof ApiError) {
    if (error.code === EXPIRED_LINK_ERROR_CODE) {
      return '만료된 페이지입니다.';
    }

    if (error.code === INVALID_LINK_ERROR_CODE) {
      return '유효하지 않은 페이지입니다.';
    }

    if (error.code === USED_LINK_ERROR_CODE) {
      return '이미 사용한 페이지입니다.';
    }

    return '만료된 페이지입니다.';
  }

  return '만료된 페이지입니다.';
}

function validatePasswordPolicy(password: string) {
  if (password.length < 8) {
    return '비밀번호는 8자 이상으로 입력해주세요.';
  }

  if (!/[A-Za-z]/.test(password) || !/\d/.test(password) || !/[^A-Za-z0-9]/.test(password)) {
    return '비밀번호는 영문, 숫자, 특수문자를 모두 포함해야 합니다.';
  }

  return '';
}

function getPageTexts(mode: EmailLinkMode) {
  if (mode === 'signup') {
    return {
      confirmLabel: '비밀번호 확인',
      heading: '비밀번호 설정',
      passwordLabel: '비밀번호 설정',
      submit: '비밀번호 설정',
      submitting: '설정 중...',
      success: '비밀번호가 설정되었습니다. 로그인 화면으로 이동합니다.',
    };
  }

  return {
    confirmLabel: '신규 비밀번호 확인',
    heading: '비밀번호 재설정',
    passwordLabel: '신규 비밀번호',
    submit: '비밀번호 변경',
    submitting: '변경 중...',
    success: '비밀번호가 변경되었습니다. 로그인 화면으로 이동합니다.',
  };
}

function PasswordResetPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [urlToken] = useState(() => searchParams.get('urlToken') ?? '');
  const [accountLabel, setAccountLabel] = useState('링크 확인 중...');
  const [linkMode, setLinkMode] = useState<EmailLinkMode>(() =>
    location.pathname.includes('signup') ? 'signup' : 'reset'
  );
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [linkErrorMessage, setLinkErrorMessage] = useState('');
  const [noticeMessage, setNoticeMessage] = useState('');
  const [isTokenVerifying, setIsTokenVerifying] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const pageTexts = getPageTexts(linkMode);

  useEffect(() => {
    let isMounted = true;

    if (!urlToken) {
      setLinkErrorMessage('유효하지 않은 페이지입니다.');
      setAccountLabel('확인 불가');
      setIsTokenVerifying(false);
      return () => {
        isMounted = false;
      };
    }

    verifyPasswordResetToken(urlToken)
      .then((response) => {
        if (isMounted) {
          if (!response.isValid) {
            setLinkErrorMessage('유효하지 않은 페이지입니다.');
            setAccountLabel('확인 불가');
            return;
          }

          setLinkErrorMessage('');
          setLinkMode(response.inviteType === 'INITIAL_SET' ? 'signup' : 'reset');
          setAccountLabel(response.account ?? response.email ?? '계정 정보 없음');
        }
      })
      .catch((error) => {
        if (isMounted) {
          setLinkErrorMessage(getPasswordLinkErrorMessage(error));
          setAccountLabel('확인 불가');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsTokenVerifying(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [urlToken]);

  const submitPasswordReset = async () => {
    setErrorMessage('');
    setNoticeMessage('');

    if (!password || !passwordConfirm) {
      setErrorMessage('신규 비밀번호와 비밀번호 확인을 모두 입력해주세요.');
      return;
    }

    const policyMessage = validatePasswordPolicy(password);

    if (policyMessage) {
      setErrorMessage(policyMessage);
      return;
    }

    if (password !== passwordConfirm) {
      setErrorMessage('신규 비밀번호와 비밀번호 확인이 일치하지 않습니다.');
      return;
    }

    setIsSubmitting(true);

    try {
      await resetPasswordByEmailLink(urlToken, password);
      setNoticeMessage(pageTexts.success);
      globalThis.setTimeout(() => {
        navigate(LOGIN_ROUTE_PATH, { replace: true });
      }, 500);
    } catch (error) {
      if (error instanceof ApiError && error.code === EXPIRED_LINK_ERROR_CODE) {
        setLinkErrorMessage('만료된 페이지입니다.');
      } else {
        setErrorMessage(getPasswordResetErrorMessage(error));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit: ComponentProps<'form'>['onSubmit'] = (event) => {
    event.preventDefault();
    void submitPasswordReset();
  };

  const isSubmitDisabled = isTokenVerifying || isSubmitting || !urlToken;

  const handleClosePage = () => {
    globalThis.close();
    globalThis.setTimeout(() => {
      navigate(LOGIN_ROUTE_PATH, { replace: true });
    }, 200);
  };

  return (
    <main className="auth-page">
      <section className="auth-panel password-change-panel" aria-labelledby="password-reset-page-title">
        <div className="auth-heading password-change-heading">
          <p>TimingHub</p>
          <h1 id="password-reset-page-title">{pageTexts.heading}</h1>
        </div>

        {linkErrorMessage && !isTokenVerifying ? (
          <div className="auth-form">
            <p className="auth-error">{linkErrorMessage}</p>
            <button className="auth-submit" type="button" onClick={handleClosePage}>
              닫기
            </button>
          </div>
        ) : (
        <form className="auth-form" onSubmit={handleSubmit}>
          <label className="auth-field">
            <span>사용자 계정</span>
            <input type="text" value={accountLabel} readOnly />
          </label>

          <label className="auth-field">
            <span>{pageTexts.passwordLabel}</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="영문, 숫자, 특수문자 포함 8자 이상"
              autoComplete="new-password"
            />
          </label>

          <label className="auth-field">
            <span>{pageTexts.confirmLabel}</span>
            <input
              type="password"
              value={passwordConfirm}
              onChange={(event) => setPasswordConfirm(event.target.value)}
              placeholder="신규 비밀번호를 다시 입력하세요"
              autoComplete="new-password"
            />
          </label>

          {errorMessage && <p className="auth-error">{errorMessage}</p>}
          {noticeMessage && <p className="auth-notice">{noticeMessage}</p>}

          <button className="auth-submit" type="submit" disabled={isSubmitDisabled}>
            {isSubmitting ? pageTexts.submitting : pageTexts.submit}
          </button>
        </form>
        )}
      </section>
    </main>
  );
}

export default PasswordResetPage;
